import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { InsertIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertIngestionConfigInput {
  // The config blob consumers hold is a parsed JSON object (`JSON.parse` /
  // spread literal); the tRPC procedure input is the shared `JSONB` type. The
  // runtime values are identical, so the public input takes the loose shape and
  // we narrow to `JSONB` at the single mutateAsync boundary below.
  IngestionConfig?: Record<string, unknown> | null;
  // Write-only: stored in Secrets Manager server-side, never returned.
  ApiKey?: string | null;
}

/**
 * Maps the tRPC mutation response (`{ Id }`) to the GraphQL mutation response
 * shape so consumers branching on the result do not need to know which path
 * executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertIngestionConfigMutation => ({
  insertChildIngestionConfig: {
    __typename: 'IdOutput',
    Id: data.Id,
  },
});

export const useInsertIngestionConfigTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.ingestionConfig.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.ingestionConfig.getAll.queryKey(),
        });
      },
    }),
  });

  return {
    insertIngestionConfig: async (
      input: InsertIngestionConfigInput
    ): Promise<InsertIngestionConfigMutation> => {
      const result = await mutation.mutateAsync({
        // Loose parsed-JSON object → shared JSONB; identical at runtime.
        IngestionConfig: (input.IngestionConfig ?? null) as JSONB | null,
        ApiKey: input.ApiKey ?? null,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
